import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PostCard from '@/components/base/PostCard';
import AdSlot from '@/pages/home/components/AdSlot';
import { categories } from '@/mocks/categories';
import { getPublishedPosts } from '@/utils/scheduler';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import BreadcrumbSchema from '@/components/base/BreadcrumbSchema';

const BASE_URL = 'https://roameurope.blog';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const published = useMemo(() => getPublishedPosts(), []);
  const filtered = published.filter((p) => p.category === slug);

  useSeoMeta({
    title: category ? `${category.name} — Relationship Articles` : 'Category',
    description: category
      ? `${category.description} — Browse all ${category.name} articles on roameurope.blog.`
      : 'Browse relationship articles by topic.',
    image: category?.ogImage,
    url: `/category/${slug}`,
    type: 'website',
    tags: category
      ? [category.name, 'relationship advice', 'dating tips', 'roameurope.blog']
      : [],
  });

  // Schema.org CollectionPage structured data
  const schemaJson = category
    ? JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${category.name} — roameurope.blog`,
        description: category.description,
        url: `${BASE_URL}/category/${category.slug}`,
        image: {
          '@type': 'ImageObject',
          url: category.ogImage,
          width: 1200,
          height: 630,
        },
        publisher: {
          '@type': 'Organization',
          name: 'roameurope.blog',
          url: BASE_URL,
        },
        hasPart: filtered.map((post) => ({
          '@type': 'Article',
          headline: post.title,
          url: `${BASE_URL}/post/${post.slug}`,
          image: post.image,
          author: { '@type': 'Person', name: post.author },
        })),
      })
    : null;

  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: category?.name ?? (slug ?? 'Category'), path: `/category/${slug}` },
  ];

  return (
    <>
      {schemaJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaJson }}
        />
      )}
      <Navbar />
      <main className="pt-24 min-h-screen">
        {/* Category hero strip */}
        {category && (
          <div
            className="w-full relative overflow-hidden mb-10"
            style={{ background: `linear-gradient(135deg, ${category.color}18 0%, ${category.color}08 100%)` }}
          >
            <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 pb-2">
              <BreadcrumbSchema items={breadcrumbItems} />
            </div>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex items-center gap-6">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${category.color}20` }}
              >
                <i
                  className={`text-2xl ${
                    slug === 'dating' ? 'ri-heart-line'
                    : slug === 'communication' ? 'ri-chat-heart-line'
                    : slug === 'breakups' ? 'ri-seedling-line'
                    : slug === 'marriage' ? 'ri-heart-2-line'
                    : 'ri-sparkling-line'
                  }`}
                  style={{ color: category.color }}
                />
              </div>
              <div>
                <h1
                  className="text-3xl md:text-4xl font-bold text-stone-900 dark:text-stone-50 mt-2"
                  style={{ fontFamily: "'Lora', serif" }}
                >
                  {category.name}
                </h1>
                <p className="text-stone-500 dark:text-stone-400 mt-1 text-sm">{category.description}</p>
              </div>
              <div className="ml-auto hidden md:block">
                <span
                  className="text-4xl font-bold opacity-10 select-none"
                  style={{ color: category.color, fontFamily: "'Lora', serif" }}
                >
                  {filtered.length} articles
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {!category && (
            <div className="mb-8">
              <BreadcrumbSchema items={breadcrumbItems} />
              <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mt-3" style={{ fontFamily: "'Lora', serif" }}>
                {slug}
              </h1>
            </div>
          )}

          <AdSlot slot="header" className="mb-8" />

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-stone-400">
              <i className="ri-article-line text-4xl mb-3 block" />
              <p>No posts in this category yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
